import {
  BaseMessageProps,
  BaseMessageOptions,
  MessageColor,
  MessageListener,
  MessageInput,
} from '@/shared/types/message';
import { messageConfig } from '../config/message.config';

/**
 * Servicio base para el sistema de mensajes
 * Implementa el patrón Factory/Strategy para permitir extensiones
 */
class MessageService {
  private listeners: MessageListener[] = [];
  private idCounter = 0;
  private recentMessages = new Map<string, number>();

  /**
   * Muestra un mensaje con el color y opciones especificados
   */
  show(
    messageOrOptions: MessageInput,
    color: MessageColor = 'success',
    options: BaseMessageOptions = {}
  ): number {
    const id = Date.now() + ++this.idCounter;

    // Determinar si el primer parámetro es un string o un objeto
    let message: string;
    let finalOptions: BaseMessageOptions;

    if (typeof messageOrOptions === 'string') {
      message = '';
      finalOptions = { ...options, title: messageOrOptions };
    } else {
      message = messageOrOptions.message ?? '';
      finalOptions = { ...options, ...messageOrOptions };
    }

    // Verificar duplicados recientes (mejorado: limpieza automática más eficiente)
    const messageKey = `${message || finalOptions.title || ''}-${color}`;
    const now = Date.now();
    const lastShown = this.recentMessages.get(messageKey);

    if (lastShown && now - lastShown < 100) {
      return id;
    }

    this.recentMessages.set(messageKey, now);

    // Limpiar cache antiguo (solo si hay muchas entradas para evitar iteraciones innecesarias)
    if (this.recentMessages.size > 50) {
      for (const [key, timestamp] of this.recentMessages.entries()) {
        if (now - timestamp > 1000) {
          this.recentMessages.delete(key);
        }
      }
    }

    const messageObj: BaseMessageProps = {
      id,
      message,
      title: finalOptions.title,
      description: finalOptions.description,
      color,
      onClick: finalOptions.onClick,
      onClose: finalOptions.onClose,
      onPrimaryClick: finalOptions.onPrimaryClick,
      onSecondaryClick: finalOptions.onSecondaryClick,
      showCloseButton: finalOptions.showCloseButton ?? true,
      isLeaving: false,
      position: finalOptions.position ?? messageConfig.position,
      variant: finalOptions.variant ?? messageConfig.variant,
      primaryButtonText: finalOptions.primaryButtonText ?? messageConfig.primaryButtonText,
      secondaryButtonText: finalOptions.secondaryButtonText ?? messageConfig.secondaryButtonText,
      size: finalOptions.size ?? messageConfig.size,
      duration:
        finalOptions.duration !== undefined ? finalOptions.duration : messageConfig.duration,
      animation: finalOptions.animation,
    };

    // Validación en desarrollo
    if (process.env.NODE_ENV !== 'production') {
      if (
        messageObj.duration !== undefined &&
        (isNaN(messageObj.duration) || !isFinite(messageObj.duration) || messageObj.duration < 0)
      ) {
        // eslint-disable-next-line no-console
        console.warn(`[MessageService] Invalid duration "${messageObj.duration}". Using default.`);
        messageObj.duration = messageConfig.duration;
      }
      if (
        messageObj.position &&
        ![
          'top-left',
          'top-center',
          'top-right',
          'center-left',
          'center',
          'center-right',
          'bottom-left',
          'bottom-center',
          'bottom-right',
        ].includes(messageObj.position)
      ) {
        // eslint-disable-next-line no-console
        console.warn(`[MessageService] Invalid position "${messageObj.position}". Using default.`);
        messageObj.position = messageConfig.position;
      }
      if (
        messageObj.color &&
        !['success', 'danger', 'info', 'warning', 'neutral', 'brand'].includes(messageObj.color)
      ) {
        // eslint-disable-next-line no-console
        console.warn(`[MessageService] Invalid color "${messageObj.color}".`);
      }
    }

    this.listeners.forEach((fn) => fn(messageObj));
    return id;
  }

  /**
   * Muestra un mensaje de éxito
   */
  success(messageOrOptions: MessageInput, options?: BaseMessageOptions): number {
    return this.show(messageOrOptions, 'success', options);
  }

  /**
   * Muestra un mensaje de error
   */
  error(messageOrOptions: MessageInput, options?: BaseMessageOptions): number {
    return this.show(messageOrOptions, 'danger', options);
  }

  /**
   * Muestra un mensaje de información
   */
  info(messageOrOptions: MessageInput, options?: BaseMessageOptions): number {
    return this.show(messageOrOptions, 'info', options);
  }

  /**
   * Muestra un mensaje de advertencia
   */
  warning(messageOrOptions: MessageInput, options?: BaseMessageOptions): number {
    return this.show(messageOrOptions, 'warning', options);
  }

  /**
   * Muestra un mensaje neutral
   */
  neutral(messageOrOptions: MessageInput, options?: BaseMessageOptions): number {
    return this.show(messageOrOptions, 'neutral', options);
  }

  /**
   * Muestra un mensaje con estilo de marca (brand)
   */
  brand(messageOrOptions: MessageInput, options?: BaseMessageOptions): number {
    return this.show(messageOrOptions, 'brand', options);
  }

  /**
   * Cierra un mensaje por su ID
   */
  dismiss(id: number): void {
    this.listeners.forEach((fn) =>
      fn({
        id,
        message: '',
        title: '',
        color: 'success',
        position: messageConfig.position,
        showCloseButton: true,
        isLeaving: true,
        variant: messageConfig.variant,
      })
    );
  }

  /**
   * Configura las opciones por defecto del servicio
   */
  configure(options: Partial<typeof messageConfig>): void {
    Object.assign(messageConfig, options);
  }

  /**
   * Obtiene los listeners registrados
   */
  getListeners(): MessageListener[] {
    return this.listeners;
  }

  /**
   * Registra un nuevo listener
   */
  subscribe(listener: MessageListener): () => void {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }
}

/**
 * Instancia singleton del servicio de toasts; la usan `toast`, `notify` y `useMessage`.
 */
export const messageService = new MessageService();

/**
 * Expone el mismo singleton que `messageService` (útil en pruebas o integración imperativa sin React).
 *
 * @returns Instancia de `MessageService` compartida en toda la app.
 */
export const useMessageService = () => {
  return messageService;
};
